import { useBoundingRect } from '@deltablue/solidjs-hooks';
import { FaSolidChevronRight } from 'solid-icons/fa';
import type { JSXElement, ParentComponent} from 'solid-js';
import { createEffect, createSignal, onCleanup, Show, splitProps } from 'solid-js';
import hamburger from '@deltablue/theme/vector/icon.hamburger.svg?url';
import { recursivelyTestChildren } from '@deltablue/web-utils';
import c from './MainHeader.module.css';

export type MainHeaderType = {
    Donut: ParentComponent<{
        fast?: JSXElement;
        special?: JSXElement;
        logo?: JSXElement;
        closeOnClick?: boolean;
    }>;
    Nav: ParentComponent;
    Item: ParentComponent;
    List: ParentComponent;
    Label: ParentComponent;
    linkClass: string;
    activeClass: string;
};

export const MainHeader: MainHeaderType = {
    Donut: p => {
        // in ms
        const animationLength = 250;
        const [isOpen, setIsOpen] = createSignal(false);
        const [busyAnimating, setBusyAnimating] = createSignal(false);
        createEffect(() => {
            isOpen();
            setBusyAnimating(true);
            setTimeout(() => {
                setBusyAnimating(false);
            }, animationLength);
        });
        const [contentSize, setMainContentSize] = createSignal<DOMRect>();
        function hookup(el: HTMLElement) {
            useBoundingRect(el, setMainContentSize);
        }
        return (
            <div
                class={c.header}
                classList={{
                    [c.opened]: isOpen(),
                    [c.hasFast]: p.fast !== undefined,
                    [c.hasSpecial]: p.special !== undefined,
                }}
                onClick={p.closeOnClick ? () => setIsOpen(false) : undefined}
            >
                <div class={c.inner}>
                    <div class={c.logo}>
                        {p.logo}
                    </div>
                    <Show when={p.fast}>
                        <div class={c.fast}>
                            {p.fast}
                        </div>
                    </Show>
                    <img
                        class={c.hamburger}
                        onClick={e => {
                            e.stopPropagation();
                            setIsOpen(o => !o);
                        }}
                        src={hamburger}
                    />
                </div>
                <div
                    class={c.main}
                    style={{
                        '--content-height': `${contentSize()?.height}px`,
                        '--transition-length': busyAnimating() ? `${animationLength}ms` : '0ms',
                    }}
                >
                    <div
                        class={c.mainInner}
                        ref={hookup}
                    >
                        <Show when={p.special}>
                            <div class={c.special}>
                                {p.special}
                            </div>
                        </Show>
                        {p.children}
                    </div>
                </div>
            </div>
        );
    },
    Nav: p => (
        <nav
            {...p}
            class={c.nav}
        />
    ),
    Item: p => {
        const [, o] = splitProps(p, ['children']);
        const [hasList, setHasList] = createSignal(false);
        const [showList, setShowList] = createSignal(false);
        function updateHasList(el: Element) {
            setHasList(recursivelyTestChildren(el, el => el.classList.contains(c.list)));
        }
        function hookup(el: HTMLElement) {
            updateHasList(el);
            const observer = new MutationObserver(() => {
                updateHasList(el);
            });
            observer.observe(el, { childList: true, subtree: true });
            onCleanup(() => observer.disconnect());
        }
        return (
            <div
                {...o}
                ref={hookup}
                class={c.item}
                classList={{
                    [c.hasList]: hasList(),
                    [c.showList]: showList(),
                }}
            >
                {p.children}
                <Show when={hasList()}>
                    <div class={c.arrow} onClick={() => setShowList(s => !s)}>
                        <FaSolidChevronRight class={c.chevron} />
                    </div>
                </Show>
            </div>
        );
    },
    List: p => {
        const [, o] = splitProps(p, ['children']);
        const [height, setHeight] = createSignal(0);
        function updateHeight(el: HTMLElement) {
            const { height } = el.getBoundingClientRect();
            setHeight(height);
        }
        function hookup(el: HTMLElement) {
            updateHeight(el);
            const observer = new ResizeObserver(entries => {
                updateHeight(entries[0].target as HTMLElement);
            });
            observer.observe(el);
            onCleanup(() => observer.disconnect());
        }
        return (
            <div {...o} class={c.list} style={{ '--opened-height': `${height()}px` }}>
                <div ref={hookup} class={c.listInner}>
                    {p.children}
                </div>
            </div>
        );
    },
    Label: p => (
        <label
            {...p}
            class={c.label}
        />
    ),
    linkClass: c.link,
    activeClass: c.active,
};

// special exports because Astro needs it to make client:load/only work
export const MainHeaderDonut = MainHeader.Donut;
export const MainHeaderItem = MainHeader.Item;
export const MainHeaderList = MainHeader.List;
